import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { RememberedBodySource } from 'services/sync-revision-decisions';

// df-704db86556f7: the extra context the `plan.orphan-subslot-at-save` canary carries, so an
// event can say WHICH route left an unreferenced plan.subSlots entry behind, not only that one
// did. Everything here is ids, enums and booleans; the plan document never goes into the report.
//
// Kept free of Angular DI so it can be unit-tested under the ts-node loader.

/** Where an orphaned sub-slot id came from, judged against the plan as the database last held it. */
export enum OrphanSubSlotOrigin {
  /** the stored copy already held the entry and no stored workout listed it: it arrived that
   *  way, from another writer or a synced revision, and this device only carried it to the save */
  ArrivedOrphaned = 'arrived-orphaned',
  /** the stored copy held the entry AND a stored workout listed it: the in-memory workout list
   *  lost the reference on this device */
  ReferenceDroppedHere = 'reference-dropped-here',
  /** the stored copy had no entry under this id: something on this device created it */
  EntryCreatedHere = 'entry-created-here',
  /** the database holds no remembered copy to compare against */
  Unknown = 'unknown',
}

/** Which caller asked for the save. A fixed label per site; every unlabelled caller is `Unknown`. */
export enum PlanSaveCaller {
  Unknown = 'unknown',
  WorkoutConcludeSet = 'workout.conclude-set',
  WorkoutVolumeAutoregulationOffer = 'workout.volume-autoregulation-offer',
  WorkoutPlateauAnswer = 'workout.plateau-answer',
  WorkoutAutoregulationAccepted = 'workout.autoregulation-accepted',
  WorkoutAutoregulationDeclined = 'workout.autoregulation-declined',
  WorkoutOpenExercisePlateauResolved = 'workout.open-exercise-plateau-resolved',
  WorkoutOpenExercisePlateauKept = 'workout.open-exercise-plateau-kept',
}

/** What the open workout page knows about one sub-slot id. */
export interface PlanSaveSubSlotFacts {
  /** the open workout has a logged exercise with this sub-slot id */
  shownOnPage: boolean;
  /** set only when shown: the loader judged the program no longer holds it (#4614) */
  missingFromPlan?: boolean;
  /** set only when shown: the page draws it from a finished session's own snapshot (#4939) */
  historical?: boolean;
}

/** Optional context a caller hands `savePlanAsync`, so the canary can name the route. */
export interface PlanSaveContext {
  caller: PlanSaveCaller;
  /** the workout page answers this for each orphan id */
  describeSubSlot?: (subSlotId: string) => PlanSaveSubSlotFacts;
  /** the plan's `_rev` when the workout page last built its exercises */
  pageLoadPlanRev?: string;
  /** the plan object the page reads and writes; compared by identity with the one the save
   *  persists, because writes into any other object never reach the program */
  pagePlan?: FitnessPlan;
}

/** The plan as the database last held it, and where this device got that copy. */
export interface StoredPlanBody {
  body: FitnessPlan;
  source: RememberedBodySource;
}

/** Classify each orphan id against the stored copy. */
export function classifyOrphanSubSlotOrigins(
  orphanIds: string[],
  stored: FitnessPlan | undefined,
): { [subSlotId: string]: OrphanSubSlotOrigin } {
  const origins: { [subSlotId: string]: OrphanSubSlotOrigin } = {};
  // guard a damaged stored copy the same way as a missing one: nothing to compare against
  const storedSubSlots = stored?.subSlots && typeof stored.subSlots === 'object' ? stored.subSlots : undefined;
  const storedWorkouts = Array.isArray(stored?.workouts) ? stored.workouts : undefined;
  for (const id of orphanIds) {
    if (!storedSubSlots || !storedWorkouts) {
      origins[id] = OrphanSubSlotOrigin.Unknown;
    } else if (!storedSubSlots[id]) {
      origins[id] = OrphanSubSlotOrigin.EntryCreatedHere;
    } else if (storedWorkouts.some(w => Array.isArray(w?.subSlotIds) && w.subSlotIds.includes(id))) {
      origins[id] = OrphanSubSlotOrigin.ReferenceDroppedHere;
    } else {
      origins[id] = OrphanSubSlotOrigin.ArrivedOrphaned;
    }
  }
  return origins;
}

/**
 * The fields added to the canary's context. Called AFTER the heal, so the orphan entries are
 * already gone from `savingPlan`; only their ids are left to work with.
 */
export function describeOrphanSubSlotsAtSave(
  orphanIds: string[],
  savingPlan: FitnessPlan,
  stored: StoredPlanBody | undefined,
  context: PlanSaveContext | undefined,
): { [key: string]: unknown } {
  const storedRev = stored?.body?._rev;
  const fields: { [key: string]: unknown } = {
    orphanOrigins: classifyOrphanSubSlotOrigins(orphanIds, stored?.body),
    storedSource: stored?.source ?? 'none',
    storedRevMatchesSavedPlan: stored ? storedRev === savingPlan._rev : undefined,
    caller: context?.caller ?? PlanSaveCaller.Unknown,
  };
  // if the workout page called, add what it knows about each orphan id and about its plan
  if (context?.describeSubSlot) {
    const pageFacts: { [subSlotId: string]: PlanSaveSubSlotFacts } = {};
    for (const id of orphanIds) pageFacts[id] = context.describeSubSlot(id);
    fields.pageSubSlotFacts = pageFacts;
  }
  if (context?.pagePlan) {
    fields.pageHoldsSavedPlanObject = context.pagePlan === savingPlan;
  }
  if (context && 'pageLoadPlanRev' in context) {
    fields.pageLoadPlanRev = context.pageLoadPlanRev ?? 'none';
    fields.storedRev = storedRev ?? 'none';
    // a stored revision different from the one the page loaded with means the program moved
    // after the page built its exercises — by this device's own saves or by an arrival; the
    // stored source says which wrote the latest one
    fields.planRevChangedSinceLoad = context.pageLoadPlanRev !== storedRev;
  }
  return fields;
}
